import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 0;
  background: var(--gradient-surface);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 1200px;
    height: 1200px;
    border-radius: 50%;
    background: var(--gradient-primary);
    top: -600px;
    right: -300px;
    opacity: 0.05;
    animation: float 8s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Content = styled.div`
  animation: slideIn var(--transition-normal) forwards;

  h2 {
    font-size: 42px;
    font-weight: 800;
    margin-bottom: 24px;
    line-height: 1.2;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 18px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    line-height: 1.6;
  }
`;

const IPadFrame = styled.div`
  background: var(--surface);
  border-radius: 40px;
  padding: 20px;
  box-shadow: var(--shadow-lg);
  width: 700px;
  max-width: 100%;
  transform: rotate(-5deg);
  transition: all var(--transition-normal);
  border: 1px solid var(--border);
  animation: fadeIn var(--transition-normal) forwards;

  &:hover {
    transform: rotate(0deg) translateY(-10px);
    box-shadow: var(--shadow-primary);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    border-radius: 4px 4px 0 0;
  }

  @media (max-width: 968px) {
    transform: none;
    margin: 40px auto;

    &:hover {
      transform: translateY(-10px);
    }
  }
`;

const Screen = styled.div`
  background: var(--background);
  border-radius: 30px;
  overflow: hidden;
  height: 400px;
  padding: 20px;
  transition: all var(--transition-normal);
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  color: var(--text-secondary);
  padding: 6px 12px;
  background: var(--primary-light);
  border-radius: 8px;
  font-weight: 500;
`;

const FilterActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  span {
    cursor: pointer;
    transition: all var(--transition-normal);
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;

    &.search {
      color: var(--text-secondary);
      &:hover {
        background: var(--surface-hover);
        color: var(--primary);
      }
    }

    &.reset {
      color: var(--error);
      &:hover {
        background: var(--error);
        color: white;
      }
    }
  }
`;

const Table = styled.div`
  width: 100%;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  padding: 16px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
  align-items: center;
  transition: all var(--transition-normal);
  color: var(--text-primary);

  &:hover {
    background: var(--surface-hover);
    transform: translateX(4px);
  }

  span {
    &.cycle {
      color: var(--primary);
      font-weight: 500;
    }
    
    &.price {
      color: var(--success);
      font-weight: 500;
    }
  }
`;

const FilterPanel = styled.div`
  position: absolute;
  right: 20px;
  top: 60px;
  background: var(--surface);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  padding: 20px;
  width: 280px;
  border: 1px solid var(--border);
  animation: slideIn var(--transition-normal) forwards;

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 16px;
  }

  label {
    display: block;
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  select {
    width: 100%;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--background);
    color: var(--text-primary);
    font-size: 14px;
    transition: all var(--transition-normal);

    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px var(--primary-light);
      outline: none;
    }
  }
`;

const AdvancedFilters = () => {
  return (
    <Section>
      <Container>
        <Content>
          <h2>Find What You Need with Advanced Filters</h2>
          <p>
            Quickly search, sort, and filter your subscriptions by cycle, tags,
            payment methods, and more.
          </p>
          <p>
            Locate specific subscriptions or upcoming renewals in seconds.
          </p>
        </Content>

        <IPadFrame>
          <Screen>
            <FilterHeader>
              <ActiveFilters>
                Active filters • Cycle: Monthly
              </ActiveFilters>
              <FilterActions>
                <span className="search">🔍 Search</span>
                <span className="reset">Reset ×</span>
              </FilterActions>
            </FilterHeader>

            <Table>
              <TableHeader>
                <span>Name</span>
                <span>Provider</span>
                <span>Cycle</span>
                <span>Price</span>
                <span>Method</span>
              </TableHeader>
              <TableRow>
                <span>Hosting</span>
                <span>Digital Ocean</span>
                <span className="cycle">Monthly</span>
                <span className="price">$29.00</span>
                <span>Bank Transfer</span>
              </TableRow>
              <TableRow>
                <span>Movies Streaming</span>
                <span>Netflix</span>
                <span className="cycle">Monthly</span>
                <span className="price">$15.49</span>
                <span>Wise Bank Card</span>
              </TableRow>
              <TableRow>
                <span>Phone Bill</span>
                <span>Vodafone</span>
                <span className="cycle">Monthly</span>
                <span className="price">$39.00</span>
                <span>Wise Bank Card</span>
              </TableRow>
            </Table>

            <FilterPanel>
              <h4>Filters</h4>
              <div>
                <label>Cycle</label>
                <select>
                  <option>Monthly</option>
                  <option>Yearly</option>
                </select>
              </div>
            </FilterPanel>
          </Screen>
        </IPadFrame>
      </Container>
    </Section>
  );
};

export default AdvancedFilters; 